import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  useTheme,
  alpha,
  InputAdornment,
  TextField,
  Menu,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import ArticleIcon from '@mui/icons-material/Article';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentPage, setLoading, setError, fetchFilteredBlogs } from '../redux/slices/blogSlice';
import { blogService } from '../services/blog';
import SortIcon from '@mui/icons-material/Sort';
import FilterListIcon from '@mui/icons-material/FilterList';
import { toast } from 'react-toastify';
import BlogFilterDrawer from '../components/Blog/BlogFilterDrawer';
import { debounce } from 'lodash';

const HeroSection = styled(Box)(({ theme }) => ({
  background: theme.palette.mode === 'dark'
    ? `linear-gradient(to bottom right, ${alpha(theme.palette.primary.dark, 0.2)}, ${alpha(theme.palette.background.default, 0.2)})`
    : `linear-gradient(to bottom right, ${alpha(theme.palette.primary.light, 0.2)}, ${alpha(theme.palette.background.paper, 0.2)})`,
  padding: theme.spacing(8, 0),
  marginBottom: theme.spacing(6),
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 50% 50%, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 50%)`,
  }
}));

const BlogCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-8px)',
    '& .MuiCardMedia-root': {
      transform: 'scale(1.05)'
    }
  }
}));

const CategoryChip = styled(Chip)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.2),
  }
}));

const SearchField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius * 3,
    backgroundColor: theme.palette.background.paper,
    transition: theme.transitions.create(['box-shadow']),
    '&:hover': {
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.1)}`
    },
    '&.Mui-focused': {
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`
    }
  }
}));

const { createFilterCriteria, FilterOperators } = blogService;

const Blog = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { blogs, loading, error, currentPage, pageSize, totalCount } = useSelector(
    (state) => state.blog
  );

  const categories = [
    { id: 'all', label: t('blog.categories.all') },
    { id: 'roommate', label: t('blog.categories.roommate') },
    { id: 'security', label: t('blog.categories.security') },
    { id: 'renting', label: t('blog.categories.renting') },
    { id: 'lifestyle', label: t('blog.categories.lifestyle') },
    { id: 'tips', label: t('blog.categories.tips') },
    { id: 'guide', label: t('blog.categories.guide') },
    { id: 'news', label: t('blog.categories.news') }
  ];

  const [filters, setFilters] = useState({
    searchText: '',
    category: '',
    startDate: '',
    endDate: '',
    tags: [],
  });
  const [sortBy, setSortBy] = useState('CreatedDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const searchInputRef = useRef(null);

  // Debounced search handler
  const debouncedSearch = useCallback(
    debounce((searchText) => {
      setFilters(prev => ({ ...prev, searchText }));
    }, 500), // 500ms bekle
    []
  );

  const handleSearch = (event) => {
    // Input değerini direkt güncelle (UI için)
    event.persist();
    // Debounced search'ü çağır
    debouncedSearch(event.target.value);
  };

  useEffect(() => {
    const filterParams = {
      filters: createFilters(),
      sortBy,
      sortOrder,
      pageIndex: currentPage,
      pageSize
    };

    blogService.getFilteredBlogs(dispatch, filterParams);
  }, [dispatch, currentPage, pageSize, sortBy, sortOrder, filters]);

  useEffect(() => {
    if (searchInputRef.current && filters.searchText) {
      searchInputRef.current.focus();
    }
  }, [filters.searchText]);

  const createFilters = () => {
    const filterArray = [];
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        if (key === 'startDate') {
          filterArray.push(
            createFilterCriteria('CreatedDate', FilterOperators.GREATER_THAN, value)
          );
        } else if (key === 'endDate') {
          filterArray.push(
            createFilterCriteria('CreatedDate', FilterOperators.LESS_THAN, value)
          );
        } else if (key === 'tags' && value.length > 0) {
          filterArray.push(
            createFilterCriteria('Tags', FilterOperators.CONTAINS, value.join(','))
          );
        } else if (key === 'searchText' && value.trim()) {
          filterArray.push(
            createFilterCriteria('Title', FilterOperators.CONTAINS, value)
          );
        } 
      }
    });
    return filterArray;
  };

  const sortOptions = [
    { value: 'CreatedDate_desc', label: t('blog.sort.newest') },
    { value: 'CreatedDate_asc', label: t('blog.sort.oldest') },
    { value: 'ViewCount_desc', label: t('blog.sort.mostViewed') },
    { value: 'ViewCount_asc', label: t('blog.sort.leastViewed') }
  ];

  const handleSortClick = (event) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleSortSelect = (option) => {
    const [field, order] = option.split('_');
    setSortBy(field);
    setSortOrder(order);
    dispatch(setCurrentPage(1));
    handleSortClose();
  };

  const handleClear = () => {
    setFilters({
      searchText: '',
      category: '',
      startDate: '',
      endDate: '',
      tags: [],
    });
    dispatch(setCurrentPage(1));
  };

  if (loading) return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '50vh',
      gap: 2
    }}>
      <CircularProgress size={40} />
      <Typography variant="body1" color="text.secondary">
        {t('common.loading')}
      </Typography>
    </Box>
  );

  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box>
      <HeroSection>
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            align="center"
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 800,
              mb: 3,
              position: 'relative',
              zIndex: 1
            }}
          >
            {t('blog.hero.title')}
          </Typography>
          <Typography
            variant="h2"
            align="center"
            sx={{
              fontSize: { xs: '1.1rem', md: '1.3rem' },
              color: 'text.secondary',
              mb: 4,
              maxWidth: 700,
              mx: 'auto',
              position: 'relative',
              zIndex: 1
            }}
          >
            {t('blog.hero.subtitle')}
          </Typography>
          <Box sx={{ maxWidth: 600, mx: 'auto', position: 'relative', zIndex: 1 }}>
            <SearchField
              inputRef={searchInputRef}
              fullWidth
              placeholder={t('blog.search')}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Container>
      </HeroSection>

      <Container maxWidth="lg">
        {/* Öne Çıkan Blog Yazıları */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h4"
            sx={{
              mb: 4,
              textAlign: 'center',
              fontWeight: 700
            }}
          >
            {t('blog.featured.title')}
          </Typography>
          <Typography
            color="text.secondary"
            sx={{ mb: 4, textAlign: 'center' }}
          >
            {t('blog.featured.description')}
          </Typography>
          <Grid container spacing={4}>
            {blogs.filter(post => post.isFeatured).slice(0, 3).map((post) => (
              <Grid item xs={12} md={4} key={post.id}>
                <BlogCard>
                  <Box sx={{ position: 'relative', paddingTop: '60%', overflow: 'hidden' }}>
                    <CardMedia
                      component="img"
                      image={post.featuredImage}
                      alt={post.title}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease-in-out'
                      }}
                    />
                    <CategoryChip
                      label={post.category}
                      sx={{
                        position: 'absolute',
                        top: 16,
                        left: 16,
                        zIndex: 1
                      }}
                    />
                  </Box>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(post.createdDate).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {post.viewCount} views
                      </Typography>
                    </Box>
                    <Typography
                      variant="h5"
                      component="h2"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {post.title}
                    </Typography>
                    <Typography
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {post.summary}
                    </Typography>
                    <Button
                      component={Link}
                      to={`/blog/${post.slug}`}
                      endIcon={<ArticleIcon />}
                      sx={{ textTransform: 'none' }}
                    >
                      {t('blog.readMore')}
                    </Button>
                  </CardContent>
                </BlogCard>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Kategorilere Göre Blog Yazıları */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h4"
            sx={{
              mb: 4,
              textAlign: 'center',
              fontWeight: 700
            }}
          >
            {t('blog.categories.title')}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {categories.map((category) => (
              <Grid item key={category.id}>
                <CategoryChip
                  label={category.label}
                  clickable
                  onClick={() => {
                    setFilters({ ...filters, category: category.id });
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  sx={{ 
                    px: 2,
                    backgroundColor: filters.category === category.id ? 
                      alpha(theme.palette.primary.main, 0.2) : 
                      alpha(theme.palette.primary.main, 0.1)
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* En Çok Okunan Blog Yazıları */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h4"
            sx={{
              mb: 4,
              textAlign: 'center',
              fontWeight: 700
            }}
          >
            {t('blog.mostRead.title')}
          </Typography>
          <Typography
            color="text.secondary"
            sx={{ mb: 4, textAlign: 'center' }}
          >
            {t('blog.mostRead.description')}
          </Typography>
          <Grid container spacing={4}>
            {[...blogs]
              .sort((a, b) => b.viewCount - a.viewCount)
              .slice(0, 3)
              .map((post) => (
                <Grid item xs={12} md={4} key={post.id}>
                  <BlogCard>
                    <Box sx={{ position: 'relative', paddingTop: '60%', overflow: 'hidden' }}>
                      <CardMedia
                        component="img"
                        image={post.featuredImage}
                        alt={post.title}
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transition: 'transform 0.3s ease-in-out'
                        }}
                      />
                      <CategoryChip
                        label={post.category}
                        sx={{
                          position: 'absolute',
                          top: 16,
                          left: 16,
                          zIndex: 1
                        }}
                      />
                    </Box>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(post.createdDate).toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {post.viewCount} views
                        </Typography>
                      </Box>
                      <Typography
                        variant="h5"
                        component="h2"
                        sx={{
                          fontWeight: 600,
                          mb: 2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {post.title}
                      </Typography>
                      <Typography
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {post.summary}
                      </Typography>
                      <Button
                        component={Link}
                        to={`/blog/${post.slug}`}
                        endIcon={<ArticleIcon />}
                        sx={{ textTransform: 'none' }}
                      >
                        {t('blog.readMore')}
                      </Button>
                    </CardContent>
                  </BlogCard>
                </Grid>
              ))}
          </Grid>
        </Box>

        {/* Tüm Blog Yazıları */}
        <Box>
          <Typography
            variant="h4"
            sx={{
              mb: 4,
              textAlign: 'center',
              fontWeight: 700
            }}
          >
            {t('blog.allPosts.title')}
          </Typography>
          <Typography
            color="text.secondary"
            sx={{ mb: 4, textAlign: 'center' }}
          >
            {t('blog.allPosts.description')}
          </Typography>

          {/* Filtre ve Sıralama Butonları */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 3 
          }}>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => setFilterOpen(true)}
            >
              {t('blog.filters.title')}
            </Button>

            <Button
              variant="outlined"
              startIcon={<SortIcon />}
              onClick={handleSortClick}
            >
              {t('blog.sort.title')}
            </Button>

            <Menu
              anchorEl={sortAnchorEl}
              open={Boolean(sortAnchorEl)}
              onClose={handleSortClose}
            >
              {sortOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  onClick={() => handleSortSelect(option.value)}
                  selected={`${sortBy}_${sortOrder}` === option.value}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Menu>
          </Box>

          {/* Blog Listesi */}
          <Grid container spacing={4}>
            {blogs.map((post) => (
              <Grid item xs={12} sm={6} md={4} key={post.id}>
                <BlogCard>
                  <Box sx={{ position: 'relative', paddingTop: '60%', overflow: 'hidden' }}>
                    <CardMedia
                      component="img"
                      image={post.featuredImage}
                      alt={post.title}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease-in-out'
                      }}
                    />
                    <CategoryChip
                      label={post.category}
                      sx={{
                        position: 'absolute',
                        top: 16,
                        left: 16,
                        zIndex: 1
                      }}
                    />
                  </Box>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(post.createdDate).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {post.viewCount} views
                      </Typography>
                    </Box>
                    <Typography
                      variant="h5"
                      component="h2"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {post.title}
                    </Typography>
                    <Typography
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {post.summary}
                    </Typography>
                    <Button
                      component={Link}
                      to={`/blog/${post.slug}`}
                      endIcon={<ArticleIcon />}
                      sx={{ textTransform: 'none' }}
                    >
                      {t('blog.readMore')}
                    </Button>
                  </CardContent>
                </BlogCard>
              </Grid>
            ))}
          </Grid>

          {/* Load More Button */}
          {blogs.length < totalCount && (
            <Box sx={{ mt: 6, textAlign: 'center' }}>
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={() => dispatch(setCurrentPage(currentPage + 1))}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: theme.shape.borderRadius * 3,
                  textTransform: 'none'
                }}
              >
                {t('blog.loadMore')}
              </Button>
            </Box>
          )}
        </Box>

        {/* Filter Drawer */}
        <BlogFilterDrawer
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          filters={filters}
          setFilters={setFilters}
          onSearch={() => {
            dispatch(setCurrentPage(1));
            setFilterOpen(false);
          }}
          onClear={handleClear}
        />
      </Container>
    </Box>
  );
};

export default Blog; 